import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementWithUserNames, ElementType, ElementTypeLabels } from '../../../../types/element.types';
import { useElementUpdates } from '../../../../hooks/dataUpdates/useElementUpdates';
import { setClipboard, getClipboard } from './useBatchPasteElements';

/**
 * 场次元素操作菜单 Hook
 * 
 * 功能：
 * - 复制分类元素到粘贴板
 * - 从粘贴板粘贴元素到分类
 * - 清空分类的所有元素
 * - 处理操作按钮点击（显示菜单）
 * - 获取当前分类的元素数量
 */
export const useElementOperations = (
  elements: ElementWithUserNames[],
  sceneId: string,
  breakdownId?: string,
  onElementRemoved?: () => void,
  setOperationsMenuVisible?: React.Dispatch<React.SetStateAction<boolean>>,
  setActiveOperationsButton?: React.Dispatch<React.SetStateAction<ElementType | null>>,
  setActiveOperationsTab?: React.Dispatch<React.SetStateAction<ElementType | null>>,
  setOperationsMenuPosition?: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number } | null>>,
  operationsButtonRefs?: React.MutableRefObject<Record<ElementType, HTMLDivElement | null>>
) => {
  const { t } = useTranslation();
  const { batchCreateElementRelationsData, batchDeleteElementRelationsData } = useElementUpdates();
  
  // 复制当前分类的所有场次元素到待粘贴板
  const handleCopyCategoryItems = useCallback((category: ElementType) => {
    const categoryItems = elements.filter(item => item.elementType === category);
    if (categoryItems.length > 0) {
      // 将分类类型信息与元素一起存储到待粘贴板
      const itemsWithCategory = categoryItems.map(item => ({
        ...item,
        sourceCategory: category // 添加分类类型信息
      }));
      setClipboard(category, itemsWithCategory);
      setOperationsMenuVisible?.(false);
      console.log(`已复制 ${categoryItems.length} 个 ${ElementTypeLabels[category]} 元素到待粘贴板`);
    } else {
      console.log(`当前没有 ${ElementTypeLabels[category]} 元素可复制`);
    }
  }, [elements, setOperationsMenuVisible]);

  // 从待粘贴板粘贴元素到当前分类
  const handlePasteCategoryItems = useCallback(async (category: ElementType) => {
    const itemsToPaste = getClipboard().get(category);
    if (!itemsToPaste || itemsToPaste.length === 0) {
      return; // 没有可粘贴的元素
    }
    
    try {
      // 获取当前场次已有的该分类的元素ID列表，用于排重
      const existingItemIds = new Set(
        elements
          .filter(item => item.elementType === category)
          .map(item => item.elementID)
      );
      
      // 过滤出需要新增的元素（不在当前场次中的）
      const itemsToAdd = itemsToPaste.filter((item) => !existingItemIds.has(item.elementID));
      
      if (itemsToAdd.length === 0) {
        console.log(`所有 ${ElementTypeLabels[category]} 元素已存在于当前场次`);
        setOperationsMenuVisible?.(false);
        return;
      }
      
      // 批量创建场次元素关系
      // 复制时包含的数量信息会被一起复制，粘贴时使用该数量创建关系
      if (breakdownId) {
        const relations = itemsToAdd.map((item: any) => ({
          elementID: item.elementID,
          sceneID: sceneId,
          elementQtyInScene: item.elementQtyInScene, // 使用复制时的数量
        }));
        
        await batchCreateElementRelationsData(breakdownId, relations);
      }

      // 通知父组件更新已选择元素列表
      onElementRemoved?.();
      
      console.log(`成功添加 ${itemsToAdd.length} 个 ${ElementTypeLabels[category]} 元素到当前场次`);
      setOperationsMenuVisible?.(false);
      
    } catch (error) {
      console.error('粘贴元素失败:', error);
      alert(t('useElementOperations.pasteFailed', { category: ElementTypeLabels[category] }) + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [elements, sceneId, breakdownId, onElementRemoved, setOperationsMenuVisible, batchCreateElementRelationsData, t]);

  // 清空当前分类的所有元素（确认由调用方 ElementsTable 的 ButtonDialog 负责，此处不再使用 window.confirm）
  const handleClearCategoryItems = useCallback(async (category: ElementType) => {
    const categoryItems = elements.filter(item => item.elementType === category);
    if (categoryItems.length === 0) {
      console.log(`当前没有 ${ElementTypeLabels[category]} 元素`);
      setOperationsMenuVisible?.(false);
      return;
    }

    try {
      // 批量删除场次元素关系（使用批量API）
      if (breakdownId) {
        const relations = categoryItems.map(item => ({
          elementID: item.elementID,
          sceneID: sceneId
        }));
        
        await batchDeleteElementRelationsData(breakdownId, relations);
      }

      // 通知父组件更新已选择元素列表
      onElementRemoved?.();
      
      console.log(`已清空 ${categoryItems.length} 个 ${ElementTypeLabels[category]} 元素`);
      setOperationsMenuVisible?.(false);
      
    } catch (error) {
      console.error(`清空${ElementTypeLabels[category]}元素失败:`, error);
      alert(t('useElementOperations.clearFailed', { category: ElementTypeLabels[category] }) + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [elements, sceneId, breakdownId, onElementRemoved, setOperationsMenuVisible, batchDeleteElementRelationsData, t]);

  // 处理操作按钮点击
  const handleOperationsClick = useCallback((category: ElementType, event: React.MouseEvent<HTMLDivElement>) => {
    // 设置active状态
    setActiveOperationsButton?.(category);
    setActiveOperationsTab?.(category);
    
    // 计算菜单位置 - 使用相对容器的定位，与内外、日夜单元格保持一致
    if (operationsButtonRefs?.current[category]) {
      const rect = operationsButtonRefs.current[category]!.getBoundingClientRect();
      // 应该相对于包含操作按钮和表格的父容器进行定位
      const containerRect = operationsButtonRefs.current[category]!.closest('.elements-content')?.getBoundingClientRect();
      if (containerRect && setOperationsMenuPosition) {
        setOperationsMenuPosition({
          top: rect.bottom - containerRect.top + 2,
          left: rect.left - containerRect.left,
          width: rect.width
        });
      }
    }
    
    // 延迟显示菜单，确保位置设置完成
    setTimeout(() => {
      setOperationsMenuVisible?.(true);
    }, 0);
    
    event.stopPropagation();
  }, [setActiveOperationsButton, setActiveOperationsTab, setOperationsMenuPosition, setOperationsMenuVisible, operationsButtonRefs]);

  // 获取当前分类的元素数量
  const getCurrentTabItemCount = useCallback((category: ElementType) => {
    const items = elements.filter(item => item.elementType === category);
    return items.length;
  }, [elements]);

  return {
    handleCopyCategoryItems,
    handlePasteCategoryItems,
    handleClearCategoryItems,
    handleOperationsClick,
    getCurrentTabItemCount,
  };
};

